import React from 'react';
import type { Announcement } from '@caro/shared/types';

interface AnnouncementBannerProps {
  announcements: Announcement[];
}

/**
 * Priority → theme-token mapping (T6.4 / D5).
 *
 * The previous palette hard-coded pastel hexes (`#FEF2F2`, `#FFF7ED`, …)
 * which inverted unreadably in Dark mode and clashed with the High-Contrast
 * black/yellow palette. The banner now derives its accent from the existing
 * semantic CSS variables (`--caro-error`, `--caro-warning`, `--caro-success`,
 * `--caro-primary`), with text in `var(--caro-text)` on a card background
 * (`var(--caro-card-bg)`) so contrast follows the active theme. A 6px
 * left border uses the accent color so the priority cue survives even when
 * the background and text remain neutral (e.g., in HC mode where the card
 * background collapses to pure black).
 */
const PRIORITY_ACCENT_VAR: Record<string, string> = {
  urgent: 'var(--caro-error)',
  high: 'var(--caro-warning)',
  normal: 'var(--caro-primary)',
  low: 'var(--caro-success)',
};

export function AnnouncementBanner({ announcements }: AnnouncementBannerProps) {
  if (!announcements || announcements.length === 0) return null;

  // Show max 2 announcements
  const visible = announcements.slice(0, 2);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
      {visible.map((announcement) => {
        const accent = PRIORITY_ACCENT_VAR[announcement.priority] || PRIORITY_ACCENT_VAR.normal;
        return (
          <div
            key={announcement.id}
            role="status"
            style={{
              // Border longhands only — using the `border` shorthand AFTER
              // `borderLeft` would clobber it (React inline `style` is just
              // a CSSOM bag, but properties applied later still win on cascade).
              padding: '12px 16px',
              backgroundColor: 'var(--caro-card-bg)',
              borderStyle: 'solid',
              borderColor: 'var(--caro-border)',
              borderTopWidth: '1px',
              borderRightWidth: '1px',
              borderBottomWidth: '1px',
              borderLeftWidth: '6px',
              borderLeftColor: accent,
              borderRadius: '8px',
              fontSize: 'var(--font-body)',
              color: 'var(--caro-text)',
              boxShadow: 'var(--caro-card-shadow, none)',
            }}
          >
            <strong style={{ color: accent }}>{announcement.title}</strong>
            {announcement.body && (
              <p style={{
                margin: '4px 0 0',
                fontSize: 'var(--font-body)',
                color: 'var(--caro-text)',
                opacity: 0.95,
              }}>
                {announcement.body}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
